import type {LegalDocumentResponse} from '@getmoss/api-gateway'
import dayjs from 'dayjs'
import type {FC} from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'
import type {LocalesType} from 'src/common/constants'
import {Icon} from 'src/common/Icon'
import {TRANSLATIONS} from '../constants'
import {
  getAvailableLanguages,
  getCategoryLabel,
  getDefaultLanguage,
  getDocumentDescription,
  getDocumentUrl,
  getSupplierLabel,
} from '../utils'

/**
 * Props for DocumentRow component
 */
interface DocumentRowProps {
  document: LegalDocumentResponse
  locale: LocalesType
}

/**
 * Component that renders a single legal document row with download options
 */
export const DocumentRow: FC<DocumentRowProps> = ({document: doc, locale}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)

  // Close popup when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsPopupOpen(false)
      }
    }

    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside)

      return () => document.removeEventListener('mousedown', handleClickOutside)
    }

    return undefined
  }, [isPopupOpen])

  // Prevent body scroll when mobile modal is open
  useEffect(() => {
    if (isPopupOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isPopupOpen])

  const formattedDate = doc.publishedAt ? dayjs(doc.publishedAt).format('DD MMM YYYY') : '-'

  const availableLanguages = useMemo(() => getAvailableLanguages(doc, locale), [doc, locale])
  const defaultLanguage = useMemo(() => getDefaultLanguage(availableLanguages, locale), [availableLanguages, locale])

  const documentDescription = getDocumentDescription(doc, defaultLanguage, locale)
  const categoryLabel = getCategoryLabel(doc.documentType, locale)
  const supplierLabel = getSupplierLabel(doc.supplier)

  return (
    <div className="group/list-item w-full py-8 xl:flex xl:items-center xl:gap-8" ref={popupRef}>
      {/* Supplier - Desktop first column */}
      <div className="hidden xl:block xl:w-[160px]">
        <p className="text-tg-body-l text-moss-black">{supplierLabel}</p>
      </div>

      {/* Mobile/Tablet: Supplier and Document title */}
      <div className="flex w-full flex-col gap-3 xl:hidden">
        <div>
          <p className="text-button uppercase leading-[14px] text-moss-grey-400">{supplierLabel}</p>
          <p className="text-tg-body-l text-moss-black">{documentDescription}</p>
        </div>

        {/* Category, Date and Button - mobile/tablet layout */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-button uppercase leading-[14px] text-moss-grey-400">{categoryLabel}</p>
            <p className="text-button uppercase leading-[14px] text-moss-grey-400">{formattedDate}</p>
          </div>

          {/* Download button - Mobile/Tablet */}
          <div>
            <button
              type="button"
              onClick={() => setIsPopupOpen(!isPopupOpen)}
              className="flex size-8 min-w-8 items-center justify-center rounded-[2px] bg-green-neon"
              aria-label={`Download ${documentDescription}`}
            >
              <Icon name="arrow" className="size-2.5 -rotate-45 text-moss-black" />
            </button>

            {isPopupOpen && (
              <div
                className="fixed inset-0 z-50 flex items-end justify-center bg-moss-black/50 px-4 pb-4"
                onClick={() => setIsPopupOpen(false)}
              >
                <div className="w-full rounded-lg bg-moss-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6">
                    <p className="text-tg-body-s mb-4 font-medium text-moss-grey-550">
                      {TRANSLATIONS.DOWNLOAD_DOCUMENT_IN[locale]}
                    </p>
                    <div className="flex flex-col gap-2">
                      {availableLanguages.map((lang) => (
                        <a
                          key={lang.code}
                          href={getDocumentUrl(doc, lang.code)}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsPopupOpen(false)}
                          className="flex items-center gap-3 rounded-md px-4 py-3 text-moss-black transition-colors hover:bg-moss-grey-100"
                        >
                          <span className="text-24">{lang.flag}</span>
                          <span className="text-tg-body-l font-medium">{lang.label}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: Document description (second column, takes remaining space) */}
      <div className="hidden xl:block xl:flex-1">
        <p className="text-tg-body-l text-moss-black">{documentDescription}</p>
      </div>

      {/* Desktop layout - Category, Date, and Download button */}
      <div className="hidden xl:flex xl:items-start xl:gap-8">
        <div className="w-[300px]">
          <p className="text-button break-words uppercase leading-[14px] text-moss-grey-400">{categoryLabel}</p>
        </div>

        <div className="w-[120px]">
          <p className="text-button uppercase leading-[14px] text-moss-grey-400">{formattedDate}</p>
        </div>

        {/* Desktop download button with popup */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsPopupOpen(!isPopupOpen)}
            className="flex size-8 min-w-8 items-center justify-center rounded-[2px] bg-green-neon transition-colors hover:bg-accent-neon-light"
            aria-label={`Download ${documentDescription}`}
          >
            <Icon name="arrow" className="size-2.5 -rotate-45 text-moss-black" />
          </button>

          {isPopupOpen && (
            <div className="absolute bottom-full right-0 mb-2 w-48 rounded-lg border border-moss-grey-150 bg-moss-white shadow-lg">
              <div className="p-4">
                <p className="text-tg-body-s mb-3 font-medium text-moss-grey-550">
                  {TRANSLATIONS.DOWNLOAD_DOCUMENT_IN[locale]}
                </p>
                <div className="flex flex-col gap-2">
                  {availableLanguages.map((lang) => (
                    <a
                      key={lang.code}
                      href={getDocumentUrl(doc, lang.code)}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsPopupOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-moss-black transition-colors hover:bg-moss-grey-100"
                    >
                      <span className="text-20">{lang.flag}</span>
                      <span className="text-tg-body-m font-medium">{lang.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
